// ============================================
// Velvet Lounge — Body-Part Click Minigame
// Replaces the brothel work encounter with an
// interactive client satisfaction mini-game.
// ============================================

import { events, Events } from '../engine/events.js';
import { storyManager } from '../engine/story.js';
import { variableManager } from '../engine/variables.js';
import { L } from '../systems/localization.js';

// ── UI tag value this module listens for ────────────────────────────────
const UI_MODE = 'velvet_work';

// ── Client type definitions ─────────────────────────────────────────────
const CLIENT_TYPES = [
    { id: 1, name: 'Shy Youth',     preferred: ['kiss','massage'], bonus: ['neck'], malus: ['anal'],        portrait: 'velvet_client_shy',      color: '#7ab0d4' },
    { id: 2, name: 'Businessman',   preferred: ['oral','chest'],   bonus: [],       malus: ['kiss'],        portrait: 'velvet_client_business',  color: '#5c6880' },
    { id: 3, name: 'Dominant',      preferred: ['oral','anal'],    bonus: [],       malus: ['kiss'],        portrait: 'velvet_client_dominant',  color: '#944060' },
    { id: 4, name: 'Romantic',      preferred: ['kiss','neck','chest'], bonus: [],  malus: ['oral'],        portrait: 'velvet_client_romantic',  color: '#c47090' },
    { id: 5, name: 'Fetishist',     preferred: ['feet','anal'],    bonus: [],       malus: ['chest'],       portrait: 'velvet_client_fetish',    color: '#886ca0' },
    { id: 6, name: 'Regular',       preferred: ['kiss','oral','anal'], bonus: [],   malus: [],              portrait: 'velvet_client_regular',   color: '#887060' },
    { id: 7, name: 'Nervous First', preferred: ['massage','kiss'], bonus: ['neck'], malus: ['anal','oral'], portrait: 'velvet_client_nervous',   color: '#70a488' },
    { id: 8, name: 'VIP',           preferred: ['anal','oral','kiss'], bonus: [],   malus: [],              portrait: 'velvet_client_vip',       color: '#c8a840' },
];

// ── Client descriptions ─────────────────────────────────────────────────
const CLIENT_DESCRIPTIONS = {
    1: "Young, fidgety. Won't make eye contact. Needs warming up.",
    2: 'All business. Checks his watch. Wants efficiency.',
    3: 'Knows what he wants. Expects obedience.',
    4: 'Brought flowers. Actually brought flowers.',
    5: 'Specific requests. Very specific.',
    6: 'Been here before. Knows the routine.',
    7: 'First time. Hands are shaking.',
    8: "Vivienne's personal referral. Expects the best.",
};

// ── Body zone definitions ───────────────────────────────────────────────
const BODY_ZONES = [
    { id: 'kiss',    label: 'Kiss',    icon: '\uD83D\uDC8B', statGain: { arousal: 2 } },
    { id: 'neck',    label: 'Neck',    icon: '\u2728',       statGain: { arousal: 2 } },
    { id: 'chest',   label: 'Chest',   icon: '\uD83D\uDC50', statGain: { arousal: 3 } },
    { id: 'oral',    label: 'Oral',    icon: '\uD83D\uDC44', statGain: { arousal: 5, sub: 1 }, stat: 'blowjob' },
    { id: 'anal',    label: 'Anal',    icon: '\uD83C\uDF51', statGain: { arousal: 5, sub: 2, shame: 1 }, stat: 'anal' },
    { id: 'feet',    label: 'Feet',    icon: '\uD83E\uDDB6', statGain: { arousal: 1 } },
    { id: 'massage', label: 'Massage', icon: '\uD83D\uDC86', statGain: { arousal: 1 } },
];

// ── Reaction texts per zone (2-3 variants each) ────────────────────────
const REACTIONS = {
    kiss:    ['His breath catches.', 'He leans into it, hungry.', 'Soft. Then harder.'],
    neck:    ['A shiver runs through him.', 'He tilts his head, exposing more.', 'Goosebumps.'],
    chest:   ['His hands find your waist.', 'A low groan.', 'He pulls you closer.'],
    oral:    ['His eyes close.', 'Fingers tangle in your hair.', 'The sounds he makes...'],
    anal:    ['He grips the sheets.', 'Time stops.', 'Everything else fades.'],
    feet:    ['He sighs, melting.', 'Unexpected intimacy.', 'His tension dissolves.'],
    massage: ['Knots release under your hands.', 'He stops talking.', 'Just breathing now.'],
};

// ── Preferred/malus reaction overlays ───────────────────────────────────
const REACTION_PREFERRED = [' He loves this.', ' You can tell he wanted this.', ' Perfect choice.'];
const REACTION_MALUS     = [' He tenses up.', " That's not what he wanted.", ' He pulls away slightly.'];
const REACTION_BONUS     = [' His whole body responds.', ' You found his weak spot.', ' He completely melts.'];

// ── Score thresholds → performance tier ─────────────────────────────────
// Calibrated for MAX_ACTIONS=5 with 7 zones: skipping 2 zones (malus + 1 neutral)
// is the optimal play. All 8 clients can reach Exquisite with optimal picks;
// a single malus typically drops one tier.
const PERFORMANCE_TIERS = [
    { min: 0, label: 'Disappointing', tier: 0, multiplier: 0.5, stars: 1 },
    { min: 4, label: 'Adequate',      tier: 1, multiplier: 1.0, stars: 2 },
    { min: 6, label: 'Impressive',    tier: 2, multiplier: 1.5, stars: 4 },
    { min: 7, label: 'Exquisite',     tier: 3, multiplier: 2.0, stars: 5 },
];

// ── Max actions per session ─────────────────────────────────────────────
const MAX_ACTIONS = 5;

// ── Discovery: shown on first encounter with a client type, before the real
// CLIENT_DESCRIPTIONS line unlocks (after the first session ends).
const UNKNOWN_CLIENT_DESC = 'New face. No tells yet.';

// ── Earliest action count where the Finish button appears.
const FINISH_UNLOCK_AT = 3;

// ============================================================================
// VelvetMinigame Class
// ============================================================================

class VelvetMinigame {
    constructor() {
        /** @type {HTMLElement|null} */
        this._root = null;

        /** @type {boolean} */
        this._isActive = false;

        /** @type {object|null} Current client data */
        this._client = null;

        /** @type {number} Total score accumulated */
        this._score = 0;

        /** @type {number} Actions used so far */
        this._actionsUsed = 0;

        /** @type {Set<string>} Zone IDs already clicked */
        this._usedZones = new Set();

        this._scope = events.createScope('velvet-minigame');
        this._injectStyles();
        this._bindEvents();
    }

    // ── Public API ──────────────────────────────────────────────────────

    /** Whether the minigame panel is currently active. */
    isActive() {
        return this._isActive;
    }

    destroy() {
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
    }

    // ── Event Bindings ──────────────────────────────────────────────────

    _bindEvents() {
        this._scope.on(Events.TAG_UI, (mode) => {
            if (mode === UI_MODE) {
                this._activate();
            } else if (this._isActive) {
                this._deactivate(false);
            }
        });
    }

    // ── Activation / Deactivation ───────────────────────────────────────

    _activate() {
        // Read client type + discovery mask from Ink variables
        let clientId = 1;
        let seenMask = 0;
        try {
            const story = storyManager.currentStory;
            if (story) {
                clientId = story.variablesState['velvet_current_client'] || 1;
                seenMask = story.variablesState['velvet_clients_seen_mask'] || 0;
            }
        } catch(e) {
            console.warn('[VelvetMinigame] Could not read velvet vars:', e);
        }

        this._client = CLIENT_TYPES.find(c => c.id === clientId) || CLIENT_TYPES[0];
        this._clientSeen = ((seenMask >> (this._client.id - 1)) & 1) === 1;
        this._score = 0;
        this._actionsUsed = 0;
        this._usedZones = new Set();
        this._isActive = true;

        // Pause story processing until minigame completes
        storyManager.waitForInput();

        // Build and show the panel
        if (!this._root) {
            this._createContainer();
        }

        // Hide #story while the minigame owns the center. Without this, a
        // compact panel leaves the prior scene image + text visible below —
        // confusing (apartment shower art behind "pleasing a client at the Velvet").
        const story = document.getElementById('story');
        if (story) {
            this._storyPrevDisplay = story.style.display || '';
            story.style.display = 'none';
        }

        this._buildUI();
        this._root.classList.remove('hidden');
    }

    _deactivate(resume = true) {
        this._isActive = false;
        if (this._root) {
            this._root.classList.add('hidden');
        }
        const story = document.getElementById('story');
        if (story) story.style.display = this._storyPrevDisplay || '';
        if (resume) {
            storyManager.releaseInput();
        }
    }

    // ── DOM Construction ────────────────────────────────────────────────

    _createContainer() {
        const el = document.createElement('div');
        el.id = 'velvet-work-panel';
        el.className = 'velvet-work-panel hidden';
        // Insert before story content so it sits above in the center column
        const story = document.getElementById('story');
        if (story) {
            story.parentNode.insertBefore(el, story);
        } else {
            document.body.appendChild(el);
        }
        this._root = el;
    }

    _buildUI() {
        const client = this._client;
        // Discovery gate: first encounter shows a generic placeholder; the
        // real CLIENT_DESCRIPTIONS line unlocks after the session bit lands
        // in velvet_clients_seen_mask. Same gate hides the preferred-zone
        // visual hints on the body buttons below.
        const desc = this._clientSeen
            ? (CLIENT_DESCRIPTIONS[client.id] || '')
            : UNKNOWN_CLIENT_DESC;
        const initial = client.name.charAt(0).toUpperCase();

        this._root.innerHTML = '';

        // ── Header ──────────────────────────────────────────────────────
        const header = document.createElement('div');
        header.className = 'velvet-client-header';

        // Portrait image with graceful fallback to the colored-initial div
        // if the PNG can't be resolved (either in tar-mode where the image
        // isn't indexed, or on file systems where it's missing). The fall-
        // back preserves the pre-2026-04-09 visual.
        const portraitUrl = this._resolveVelvetImage(client.portrait);
        const avatarHtml = portraitUrl
            ? `<div class="velvet-client-avatar velvet-client-avatar-img">
                   <img src="${portraitUrl}" alt="${client.name}"
                        onerror="var p=this.parentElement;if(p){p.style.backgroundColor='${client.color}';p.textContent='${initial}';this.remove();}">
               </div>`
            : `<div class="velvet-client-avatar" style="background-color: ${client.color}">${initial}</div>`;

        header.innerHTML = `
            ${avatarHtml}
            <div class="velvet-client-info">
                <div class="velvet-client-name">${client.name}</div>
                <div class="velvet-client-desc">${desc}</div>
            </div>
            <div class="velvet-actions-remaining" id="velvet-actions">
                Actions: <span id="velvet-action-count">${MAX_ACTIONS - this._actionsUsed}</span>/${MAX_ACTIONS}
            </div>
        `;
        this._root.appendChild(header);

        // ── Scene beat image (updated on each zone click) ───────────────
        const beatImage = document.createElement('div');
        beatImage.className = 'velvet-beat-image';
        beatImage.id = 'velvet-beat-image';
        // Initial image: show the client portrait as the "anticipation" frame.
        // On zone click, swap to the corresponding velvet_work_{zone}.png with
        // variant suffixes (chastity, longhair) resolved via AppearanceState.
        if (portraitUrl) {
            beatImage.innerHTML = `<img src="${portraitUrl}" alt="${client.name}" class="velvet-beat-img-el">`;
        }
        this._root.appendChild(beatImage);

        // ── Reaction area ───────────────────────────────────────────────
        const reaction = document.createElement('div');
        reaction.className = 'velvet-reaction';
        reaction.id = 'velvet-reaction';
        reaction.textContent = L.get('velvet_mg.reaction.intro', 'Choose how to please your client...');
        this._root.appendChild(reaction);

        // ── Body zone buttons ───────────────────────────────────────────
        const zones = document.createElement('div');
        zones.className = 'velvet-zones';

        for (const zone of BODY_ZONES) {
            const btn = document.createElement('button');
            btn.className = 'velvet-zone-btn';
            btn.dataset.zone = zone.id;

            // Preferred-zone hint dot — only shown after the client type is
            // unlocked. Bonus zone gets a brighter rose; preferred a softer
            // rose. Malus is intentionally NOT marked — players still have to
            // feel out the wrong moves.
            if (this._clientSeen) {
                if (client.bonus.includes(zone.id))    btn.classList.add('velvet-zone-bonus-hint');
                else if (client.preferred.includes(zone.id)) btn.classList.add('velvet-zone-preferred-hint');
            }

            btn.innerHTML = `<span class="velvet-zone-icon">${zone.icon}</span><span class="velvet-zone-label">${zone.label}</span>`;

            btn.addEventListener('click', () => this._onZoneClick(zone));
            zones.appendChild(btn);
        }
        this._root.appendChild(zones);

        // ── Score / Stars ───────────────────────────────────────────────
        const scoreArea = document.createElement('div');
        scoreArea.className = 'velvet-score';
        scoreArea.innerHTML = `<div class="velvet-stars" id="velvet-stars">${this._renderStars(0)}</div>`;
        this._root.appendChild(scoreArea);

        // ── Finish button (hidden until FINISH_UNLOCK_AT actions used) ──
        const finishBtn = document.createElement('button');
        finishBtn.id = 'velvet-finish-btn';
        finishBtn.className = 'velvet-finish-btn velvet-finish-btn-hidden';
        finishBtn.textContent = 'Finish here';
        finishBtn.addEventListener('click', () => {
            if (!this._isActive) return;
            this._finishSession();
        });
        this._root.appendChild(finishBtn);
    }

    // ── Zone Click Handler ──────────────────────────────────────────────

    _onZoneClick(zone) {
        if (!this._isActive || this._actionsUsed >= MAX_ACTIONS) return;
        if (this._usedZones.has(zone.id)) return;

        this._usedZones.add(zone.id);
        this._actionsUsed++;

        // Calculate points for this action
        const client = this._client;
        let points = 1; // neutral
        let reactionSuffix = '';

        if (client.bonus.includes(zone.id)) {
            points = 3;
            reactionSuffix = REACTION_BONUS[Math.floor(Math.random() * REACTION_BONUS.length)];
        } else if (client.preferred.includes(zone.id)) {
            points = 2;
            reactionSuffix = REACTION_PREFERRED[Math.floor(Math.random() * REACTION_PREFERRED.length)];
        } else if (client.malus.includes(zone.id)) {
            points = 0;
            reactionSuffix = REACTION_MALUS[Math.floor(Math.random() * REACTION_MALUS.length)];
        }
        this._score += points;

        // Pick a random base reaction for this zone
        const baseReactions = REACTIONS[zone.id] || ['...'];
        const baseText = baseReactions[Math.floor(Math.random() * baseReactions.length)];
        const fullReaction = baseText + reactionSuffix;

        // Update reaction display with fade-in
        const reactionEl = document.getElementById('velvet-reaction');
        if (reactionEl) {
            reactionEl.classList.remove('velvet-reaction-enter');
            // Force reflow to restart animation
            void reactionEl.offsetWidth;
            reactionEl.textContent = fullReaction;
            reactionEl.classList.add('velvet-reaction-enter');

            // Color-code by score: bonus=gold, preferred=rose, malus=dim, neutral=default
            reactionEl.className = 'velvet-reaction velvet-reaction-enter';
            if (points === 3) reactionEl.classList.add('velvet-reaction-bonus');
            else if (points === 2) reactionEl.classList.add('velvet-reaction-preferred');
            else if (points === 0) reactionEl.classList.add('velvet-reaction-malus');
        }

        // Disable the clicked zone button
        const btn = this._root.querySelector(`[data-zone="${zone.id}"]`);
        if (btn) {
            btn.classList.add('velvet-zone-used');
            btn.disabled = true;
        }

        // Swap the beat image to the matching velvet_work_{zone} with
        // appearance variants (chastity / longhair) applied. Falls back
        // silently (leaves the previous image) if the asset doesn't exist
        // — a handful of zones (chest, massage) don't have action images
        // yet, and anal has only variant PNGs, no base.
        this._updateBeatImage(zone.id);

        // Update action counter
        const countEl = document.getElementById('velvet-action-count');
        if (countEl) {
            countEl.textContent = MAX_ACTIONS - this._actionsUsed;
        }

        // Update stars
        this._updateStars();

        // Reveal Finish button once the player has enough datapoints to
        // judge. Stays visible until session ends.
        if (this._actionsUsed >= FINISH_UNLOCK_AT) {
            const finishBtn = document.getElementById('velvet-finish-btn');
            if (finishBtn) finishBtn.classList.remove('velvet-finish-btn-hidden');
        }

        // Check if done
        if (this._actionsUsed >= MAX_ACTIONS || this._usedZones.size >= BODY_ZONES.length) {
            // Small delay to let the player see the last reaction
            setTimeout(() => this._finishSession(), 800);
        }
    }

    // ── Stars Display ───────────────────────────────────────────────────

    _renderStars(filledCount) {
        let html = '';
        for (let i = 0; i < 5; i++) {
            html += `<span class="velvet-star ${i < filledCount ? 'velvet-star-filled' : 'velvet-star-empty'}">\u2605</span>`;
        }
        return html;
    }

    _updateStars() {
        const tier = this._getTier();
        const starsEl = document.getElementById('velvet-stars');
        if (starsEl) {
            starsEl.innerHTML = this._renderStars(tier.stars);
        }
    }

    // ── Image Resolution ────────────────────────────────────────────────

    /**
     * Resolve a velvet asset name (e.g. 'velvet_client_shy' or
     * 'velvet_work_oral') to a URL using the window-exposed
     * _pno_resolveImage helper. Returns null if the image can't be
     * found — callers fall back to colored placeholders.
     */
    _resolveVelvetImage(name) {
        if (!name) return null;
        // Use manifest to find subfolder, prefer .png (dev) with .webp fallback (prod)
        const manifest = window._imageManifest;
        if (manifest && manifest[name] !== undefined) {
            const folder = manifest[name];
            const base = folder ? `assets/images/${folder}/${name}` : `assets/images/${name}`;
            return `${base}.png`;
        }
        return null;
    }

    /**
     * Swap the beat image to the `velvet_work_{zone}` asset, with the
     * player's current appearance variants (chastity / longhair) applied
     * via suffix resolution. Tries variants in this order (most specific
     * first), falling back gracefully:
     *   velvet_work_oral_chastity_longhair
     *   velvet_work_oral_chastity
     *   velvet_work_oral_longhair
     *   velvet_work_oral
     * Leaves the previous image unchanged if none resolve.
     */
    _updateBeatImage(zoneId) {
        const beat = document.getElementById('velvet-beat-image');
        if (!beat) return;

        // Determine appearance suffix from player state.
        // F02: getValue() reads baseVar (numeric stats + names). Flags live
        // in `variableManager.flags`. Pre-fix this returned undefined for
        // both, so chastity/longhair image variants NEVER resolved — every
        // player saw the base velvet_work_X.png regardless of state.
        const wearingChastity = !!variableManager.getFlag('wearing_chastity');
        const longHair = !!variableManager.getFlag('has_long_hair');

        // Build variant candidates in specificity order
        const base = `velvet_work_${zoneId}`;
        const candidates = [];
        if (wearingChastity && longHair) candidates.push(`${base}_chastity_longhair`);
        if (wearingChastity) candidates.push(`${base}_chastity`);
        if (longHair) candidates.push(`${base}_longhair`);
        candidates.push(base);

        for (const name of candidates) {
            const url = this._resolveVelvetImage(name);
            if (url) {
                // Test-load via a temporary Image() before committing — if the
                // resolver returned a blob URL or valid path, this will
                // succeed synchronously enough for the swap to feel instant.
                // On failure (e.g. file doesn't exist yet), the onerror
                // handler restores the previous image.
                const existing = beat.querySelector('img');
                const img = document.createElement('img');
                img.src = url;
                img.alt = zoneId;
                img.className = 'velvet-beat-img-el';
                img.onload = () => {
                    if (existing) existing.remove();
                    beat.appendChild(img);
                };
                img.onerror = () => { /* keep previous image, silently skip */ };
                return;
            }
        }
    }

    _getTier() {
        // Walk thresholds in reverse to find highest matching
        for (let i = PERFORMANCE_TIERS.length - 1; i >= 0; i--) {
            if (this._score >= PERFORMANCE_TIERS[i].min) {
                return PERFORMANCE_TIERS[i];
            }
        }
        return PERFORMANCE_TIERS[0];
    }

    // ── Session Complete ────────────────────────────────────────────────

    _finishSession() {
        const tier = this._getTier();
        const baseTip = 20;
        const tipAmount = Math.round(baseTip * tier.multiplier);

        // Show completion summary briefly
        const reactionEl = document.getElementById('velvet-reaction');
        if (reactionEl) {
            reactionEl.classList.remove('velvet-reaction-enter', 'velvet-reaction-bonus',
                'velvet-reaction-preferred', 'velvet-reaction-malus');
            void reactionEl.offsetWidth;
            reactionEl.textContent = `${tier.label}. Tip: $${tipAmount}`;
            reactionEl.classList.add('velvet-reaction-enter');
            if (tier.tier >= 2) reactionEl.classList.add('velvet-reaction-bonus');
        }

        // Disable all remaining zone buttons + hide Finish (already used / no-op)
        this._root.querySelectorAll('.velvet-zone-btn:not(.velvet-zone-used)').forEach(btn => {
            btn.classList.add('velvet-zone-used');
            btn.disabled = true;
        });
        const finishBtn = document.getElementById('velvet-finish-btn');
        if (finishBtn) finishBtn.classList.add('velvet-finish-btn-hidden');

        // Update stars to final
        this._updateStars();

        // Write results to Ink variables. These are all non-observed vars, so
        // Ink-only (story.variablesState) per save-state-drift rule 1 in
        // .claude/rules/save-state-drift.md — a setValue() shadow would drift
        // on save/load.
        try {
            const story = storyManager.currentStory;
            if (story) {
                story.variablesState['velvet_performance'] = tier.tier;

                story.variablesState['velvet_tip_amount'] = tipAmount;

                // Discovery: set the bit for this client type so the next
                // encounter shows the description + zone hints. Idempotent.
                const prevMask = story.variablesState['velvet_clients_seen_mask'] || 0;
                const newMask  = prevMask | (1 << (this._client.id - 1));
                if (newMask !== prevMask) {
                    story.variablesState['velvet_clients_seen_mask'] = newMask;
                }
            }
        } catch(e) {
            console.warn('[VelvetMinigame] Could not write results to Ink:', e);
        }

        // After a pause for the player to see the summary, hide and resume story
        setTimeout(() => {
            this._deactivate(true);
        }, 1500);
    }

    // ── CSS Injection ───────────────────────────────────────────────────

    _injectStyles() {
        if (document.getElementById('velvet-minigame-styles')) return;

        const style = document.createElement('style');
        style.id = 'velvet-minigame-styles';
        style.textContent = `
/* ============================================
   Velvet Lounge Minigame Panel
   ============================================ */

.velvet-work-panel {
    background: rgba(14, 11, 20, 0.95);
    border: 1px solid rgba(196, 112, 224, 0.15);
    border-radius: 12px;
    padding: 12px 14px 14px;
    margin: 8px auto;
    max-width: 640px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(228, 140, 184, 0.05);
    /* Panel sits above #story in .center-panel (parent has overflow:hidden).
       Beat image is 4:3 aspect, so intrinsic panel height is ~750-820px on
       portrait phones. That overruns short viewports (Samsung A54 ~870px
       minus browser chrome) and the bottom button row of the 7-action grid
       gets silently clipped, leaving the player at 6/7 with no way to reach
       the 7th. Bar-minigame fits at ~400px so its no-scroll variant is fine,
       but velvet needs scroll on short viewports. flex:0 1 auto + min-height:0
       lets the parent shrink us below intrinsic height; overflow-y:auto then
       gives the panel its own scrollbar. #story stays JS-hidden in _activate()
       so we never leak the prior scene below us.
       Reported 2026-04-27 (Android 14, SM-A546B WebView, 0.2.4.2 Free): player
       could do 6 of 7 actions and the minigame "locked", 7th button clipped. */
    flex: 0 1 auto;
    min-height: 0;
    max-height: 100%;
    overflow-y: auto;
    position: relative;
}

.velvet-work-panel.hidden {
    display: none;
}

/* ── Client Header ───────────────────────── */

.velvet-client-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(196, 112, 224, 0.1);
}

.velvet-client-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    overflow: hidden;
}
/* Image variant of the client avatar — crop to circle, fill frame */
.velvet-client-avatar-img {
    background: rgba(20, 16, 32, 0.8);
    border: 2px solid rgba(196, 112, 224, 0.35);
}
.velvet-client-avatar-img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

/* Scene beat image — shown above the reaction text, swaps on each zone click.
   16:9 letterbox to match the source assets (1024x768 approx). */
.velvet-beat-image {
    width: 100%;
    max-width: 520px;
    aspect-ratio: 4 / 3;
    margin: 0 auto;
    background: rgba(20, 16, 32, 0.6);
    border: 1px solid rgba(196, 112, 224, 0.18);
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5), inset 0 0 0 1px rgba(228, 140, 184, 0.04);
}
.velvet-beat-img-el {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: opacity 0.35s ease;
}

.velvet-client-info {
    flex: 1;
    min-width: 0;
}

.velvet-client-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--text, #ddd4ec);
    margin-bottom: 2px;
}

.velvet-client-desc {
    font-size: 12px;
    color: var(--text-dim, #a89ab8);
    font-style: italic;
    line-height: 1.3;
}

.velvet-actions-remaining {
    font-size: 12px;
    color: var(--text-dim, #a89ab8);
    white-space: nowrap;
    text-align: right;
    flex-shrink: 0;
}

.velvet-actions-remaining span {
    color: var(--orchid, #c470e0);
    font-weight: 600;
}

/* ── Reaction Text ───────────────────────── */

.velvet-reaction {
    min-height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13.5px;
    font-style: italic;
    color: var(--text-body, #c8beda);
    text-align: center;
    padding: 8px 12px;
    border-radius: 8px;
    background: rgba(26, 20, 40, 0.6);
    transition: color 0.3s ease;
}

.velvet-reaction-enter {
    animation: velvet-fade-in 0.35s ease-out;
}

.velvet-reaction-bonus {
    color: #e8c860;
}

.velvet-reaction-preferred {
    color: var(--rose, #e88cb8);
}

.velvet-reaction-malus {
    color: var(--text-faint, #706480);
}

@keyframes velvet-fade-in {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
}

/* ── Zone Button Grid ────────────────────── */

.velvet-zones {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}

.velvet-zone-btn {
    min-height: 48px;
    padding: 10px 6px;
    background: linear-gradient(145deg, rgba(28, 22, 48, 0.9), rgba(20, 16, 36, 0.95));
    border: 1px solid rgba(46, 34, 66, 0.6);
    border-radius: 8px;
    color: var(--text, #ddd4ec);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    position: relative;
    overflow: hidden;
    transition: all 0.2s ease;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}

.velvet-zone-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 7px;
    background: radial-gradient(ellipse at 50% 0%, rgba(196, 112, 224, 0.04), transparent 70%);
    pointer-events: none;
}

.velvet-zone-btn:hover:not(:disabled) {
    background: rgba(36, 28, 60, 0.95);
    border-color: rgba(196, 112, 224, 0.3);
    box-shadow:
        0 4px 20px rgba(196, 112, 224, 0.1),
        0 0 40px rgba(228, 140, 184, 0.04),
        inset 0 0 20px rgba(228, 140, 184, 0.03);
    transform: translateY(-2px);
}

.velvet-zone-btn:active:not(:disabled) {
    background: #2a2040;
    border-color: var(--rose, #e88cb8);
    transform: scale(0.97);
    box-shadow: 0 0 20px rgba(228, 140, 184, 0.15), inset 0 1px 8px rgba(228, 140, 184, 0.08);
}

.velvet-zone-btn.velvet-zone-used {
    opacity: 0.3;
    pointer-events: none;
    border-color: rgba(46, 34, 66, 0.3);
    transform: none;
}

/* Preferred / bonus hints — only added in _buildUI() when the player has
   worked this client type before. Subtle: a soft rim glow + small dot in
   the top-right corner. Malus zones are intentionally NOT marked. */
.velvet-zone-preferred-hint {
    border-color: rgba(255, 168, 196, 0.45) !important;
    box-shadow: inset 0 1px 6px rgba(255, 168, 196, 0.10);
}
.velvet-zone-bonus-hint {
    border-color: rgba(255, 200, 110, 0.55) !important;
    box-shadow: inset 0 1px 8px rgba(255, 200, 110, 0.15);
}
.velvet-zone-preferred-hint::after,
.velvet-zone-bonus-hint::after {
    content: '';
    position: absolute;
    top: 6px;
    right: 6px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(255, 168, 196, 0.85);
    box-shadow: 0 0 6px rgba(255, 168, 196, 0.5);
}
.velvet-zone-bonus-hint::after {
    background: rgba(255, 200, 110, 0.95);
    box-shadow: 0 0 8px rgba(255, 200, 110, 0.6);
}
.velvet-zone-btn.velvet-zone-used.velvet-zone-preferred-hint::after,
.velvet-zone-btn.velvet-zone-used.velvet-zone-bonus-hint::after {
    opacity: 0.2;
}
/* (.velvet-zone-btn already has position:relative further up — needed
   for the ::after dot positioning.) */

.velvet-zone-icon {
    font-size: 20px;
    line-height: 1;
}

.velvet-zone-label {
    font-size: 11px;
    color: var(--text-dim, #a89ab8);
    letter-spacing: 0.3px;
}

/* ── Score / Stars ───────────────────────── */

.velvet-score {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 8px;
    border-top: 1px solid rgba(196, 112, 224, 0.1);
}

.velvet-stars {
    display: flex;
    gap: 4px;
    font-size: 22px;
}

.velvet-star {
    transition: color 0.3s ease, text-shadow 0.3s ease;
}

.velvet-star-filled {
    color: #e8c860;
    text-shadow: 0 0 8px rgba(232, 200, 96, 0.4);
}

.velvet-star-empty {
    color: rgba(100, 88, 120, 0.4);
}

/* ── Finish button — appears after FINISH_UNLOCK_AT actions ───────── */

.velvet-finish-btn {
    align-self: center;
    margin-top: 4px;
    padding: 7px 22px;
    background: linear-gradient(145deg, rgba(96, 28, 56, 0.55), rgba(60, 18, 38, 0.65));
    border: 1px solid rgba(255, 168, 196, 0.4);
    border-radius: 18px;
    color: rgba(255, 230, 240, 0.92);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.18s ease;
    opacity: 1;
    transform: translateY(0);
}

.velvet-finish-btn:hover {
    background: linear-gradient(145deg, rgba(120, 36, 70, 0.65), rgba(80, 22, 48, 0.75));
    border-color: rgba(255, 168, 196, 0.7);
    box-shadow: 0 0 14px rgba(255, 168, 196, 0.18);
}

.velvet-finish-btn-hidden {
    opacity: 0;
    transform: translateY(4px);
    pointer-events: none;
}

/* ── Mobile: 2-column grid ───────────────── */

@media (max-width: 768px) {
    .velvet-zones {
        grid-template-columns: repeat(2, 1fr);
    }

    .velvet-work-panel {
        padding: 14px;
        gap: 12px;
        margin: 4px 0;
    }

    .velvet-client-header {
        flex-wrap: wrap;
    }

    .velvet-actions-remaining {
        width: 100%;
        text-align: center;
        margin-top: 4px;
    }

    .velvet-zone-btn {
        min-height: 52px;
        padding: 12px 8px;
    }
}

/* ── Small desktop: 3-column grid ────────── */

@media (min-width: 769px) and (max-width: 1024px) {
    .velvet-zones {
        grid-template-columns: repeat(3, 1fr);
    }
}
`;
        document.head.appendChild(style);
    }
}

// ── Singleton export ────────────────────────────────────────────────────
// Hook up in main.js:
//   import { velvetMinigame } from './ui/velvet-minigame.js';
//   // The module self-registers via events.on(Events.TAG_UI) in its constructor.
//   // No additional wiring needed beyond the import (which triggers construction).
export const velvetMinigame = new VelvetMinigame();
